import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface VolcesSeedance30Params {
  /**
   * 内容数组，必填
   */
  content: Array<{
    /**
     * 内容类型，必须为text
     */
    type: string;
    /**
     * 文本内容
     */
    text: string;
  }>;
  /**
   * 分辨率
   * @values 480p | 720p | 1080p
   */
  resolution?: '480p' | '720p' | '1080p';
  /**
   * 宽高比
   * @values 21:9 | 16:9 | 4:3 | 1:1 | 3:4 | 9:16 | 9:21 | keep_ratio | adaptive
   */
  ratio?: '21:9' | '16:9' | '4:3' | '1:1' | '3:4' | '9:16' | '9:21' | 'keep_ratio' | 'adaptive';
  /**
   * 视频时长，范围3-12秒
   */
  duration?: number;
  /**
   * 帧率
   * @values 16 | 24
   */
  framespersecond?: 16 | 24;
  /**
   * 随机种子，范围-1到2^32-1
   */
  seed?: number;
  /**
   * 是否添加水印
   */
  watermark?: boolean;
  /**
   * 是否固定摄像机
   */
  camerafixed?: boolean;
  /**
   * 是否返回最后一帧图像，默认false
   */
  return_last_frame?: boolean;
}

export interface VolcesSeedance30Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_seedance_3_0
 *
 * 通过火山引擎SeeDance 3.0 API创建视频生成任务。[API Documentation](https://www.volcengine.com/docs/82379/1520757)
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesSeedance30 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesSeedance30({
 *   // 参数见 VolcesSeedance30Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesSeedance30Params 接口定义
 * @returns 生成的资源列表
 */
export async function volcesSeedance30(
  params: VolcesSeedance30Params,
): Promise<VolcesSeedance30Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_seedance_3_0',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesSeedance30Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
